import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useState } from 'react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import Spinner from '@dashboard/components/Spinner';
import TableRowElement from '@dashboard/components/TableRowElement';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  NAME_ASCENDING: {
    value: 'name',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Name A-Z',
  },
  NAME_DESCENDING: {
    value: 'name',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Name Z-A',
  },
  EMAIL_ASCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Email A-Z',
  },
  EMAIL_DESCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Email Z-A',
  },
};

export default function Customers() {
  const [sortOption, setSortOption] = useState(Object.keys(SORT_OPTIONS)[0]);
  const [search, setSearch] = useState('');

  const sort = {
    [SORT_OPTIONS[sortOption].value]: SORT_OPTIONS[sortOption].direction,
  };

  return (
    <Card variant="root">
      <CardHeader variant="root" className="gap-y-2">
        <CardHeaderTitle variant="root">Customers</CardHeaderTitle>

        <CardHeaderActions>
          <SearchInput onChange={setSearch} name="search_customers" placeholder="Search by email" />

          <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

          <div>
            <ExportView
              collection="customers"
              label="customers"
              filters={{
                sort: JSON.stringify(sort),
                search,
              }}
            />
          </div>
        </CardHeaderActions>
      </CardHeader>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="3xl">Customer</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Created date
          </CardListItemElement>
          {/* <CardListItemElement>Last charge / subscription</CardListItemElement> */}
        </CardListItem>

        <CustomerList sort={sort} search={search} />
      </CardContent>
    </Card>
  );
}

function CustomerList({ sort, search }) {
  const { sellerId } = useAuth();

  const { getInfiniteCustomers } = useCustomerQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['customers', sellerId, search, sort],
    queryFn: async ({ pageParam }) =>
      await getInfiniteCustomers({
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return (
      <CardContent variant="root">
        <Spinner />
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent variant="root">
        Unable to show customers. Please refresh or contact support.
      </CardContent>
    );
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty && search) {
    return (
      <EmptyState>
        <EmptyState.Title>No customers found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isEmpty) {
    return (
      <EmptyState>
        <EmptyState.Title>No customers yet</EmptyState.Title>
        <EmptyState.Subtitle>Your customers will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;

  return (
    <CardContent>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(customer => <CustomerListItem key={customer._id} customer={customer} />),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more customers to show</div>
      )}
    </CardContent>
  );
}

// eslint-disable-next-line react/prop-types
const CustomerListItem = ({ customer }) => (
  <CardListItem href={`/customers/${customer._id}`} key={customer._id}>
    <TableRowElement size={'3xl'}>
      <div className="font-medium">{customer.email || ''}</div>
      <div className="text-sm text-gray-500">{customer.name || ''}</div>
    </TableRowElement>

    <TableRowElement size="xl" align="right">
      {format(new Date(customer.createdAt), 'd MMM, yyyy, HH:mm')}
    </TableRowElement>
  </CardListItem>
);
